
import { namespace } from 'vuex-class';
import { Component, Mixins, Prop } from 'vue-property-decorator';

import {
	MonetizationType,
	ObjectType,
	Offer,
	Offer as OfferGraphql,
	PresentationType,
	SraEventAction,
} from '@/@types/graphql-types';
import BuyBoxBundle from '@/components/buybox/BuyBoxBundle.vue';
import BuyBoxRow from '@/components/buybox/BuyBoxRow.vue';
import {
	GetTitleOffersPromotedDocument,
	GetTitleOffersPromotedQueryVariables,
} from '@/components/buybox/graphql/queries/GetTitleOffersPromoted.query';

import {
	NarrativeObjectType,
	OfferLocation,
	getBuyboxCategoryByMonetizationGraphql,
	trackClickOutGraphql,
} from '@/helpers/offer-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { ImpressionTrackingEvents } from '@/enums/events';
import { BuyboxOfferCategory, TitleOffers } from '@/interfaces/buybox';

import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import { toastError } from '@/helpers/toast/toast-helper';
import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';

// BUYBOX_EXP_4
import { buyboxExp4TrackClickout } from '@/components/experiments/buybox-exp-4/tracking';
// BUYBOX_EXP_4

// BUYBOX_EXP_5
import { buyboxExp5TrackClickout } from '@/components/experiments/buybox-exp-5/tracking';
// BUYBOX_EXP_5

// APPLE_BACKDROP_EXP
import AppleBackdropExp from '@/components/experiments/AppleBackdropExp';
import { AppleBackdropExpPropsType } from '@/components/experiments/AppleBackdropExp/types';
// APPLE_BACKDROP_EXP
// APPLE_QUALITY_TV_EXP
import AppleQualityTvExp, { AppleQualityTvExpName } from '@/components/experiments/QualityTvExp';
import {
	GetTitleOffersDocument,
	GetTitleOffersQuery,
	GetTitleOffersQueryVariables,
} from './graphql/queries/GetTitleOffers.query';
import { useApolloData } from '@/helpers/composables/useApolloData';
import { UsePromotion, usePromotion } from '@/helpers/composables/usePromotion';
import { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import { OfferPresentationType } from '@/interfaces/titles';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { RoutingState } from '@/stores/routing.store';
// APPLE_QUALITY_TV_EXP

type TitleOffersPromotedData = {
	promotedOffers: TitleOfferFragment[];
	promotedBundles: {
		node: {
			clearName: string;
			icon: string;
			technicalName: string;
			bundleId: number;
		};
		promotionUrl: string;
		offer: TitleOfferFragment;
	}[];
};

type BuyboxRowOffer = TitleOfferFragment & {
	iconUrl?: string;
	icons?: string[];
	bundleId?: number;
	bundleTechnicalName?: string;
};

type OfferCategories =
	| BuyboxOfferCategory.STREAM
	| BuyboxOfferCategory.RENT
	| BuyboxOfferCategory.BUY
	| BuyboxOfferCategory.BUNDLE;

const Language = namespace('language');
const Routing = namespace('routing');

// APPLE_QUALTIY_TV_EXP
const Experiment = namespace('experiment');
const User = namespace('user');
// APPLE_QUALTIY_TV_EXP

const CategoryOrder = [
	BuyboxOfferCategory.STREAM,
	BuyboxOfferCategory.RENT,
	BuyboxOfferCategory.BUY,
	BuyboxOfferCategory.BUNDLE,
];

@Component({
	name: 'PromotedBuyBox',
	components: { BuyBoxRow, BuyBoxBundle },
	apollo: {
		titleOffersPromotedData: {
			query: GetTitleOffersPromotedDocument,
			variables(): GetTitleOffersPromotedQueryVariables {
				return this.queryVariables;
			},
			update(data) {
				return data.node;
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Promoted Buy Box Data]:',
					{ error: error, where: 'Component: PromotedBuyBox' },
					'error'
				);
				process.client ? this.showErrorToast() : null;
			},
		},
		// APPLE_QUALITY_TV_EXP
		offers: {
			query: GetTitleOffersDocument,
			variables(): GetTitleOffersQueryVariables {
				return this.queryOfferVariables;
			},
			update(data: GetTitleOffersQuery) {
				if (data.node == null) return;

				if (
					data.node.__typename === 'Episode' ||
					data.node.__typename === 'Movie' ||
					data.node.__typename === 'Season' ||
					data.node.__typename === 'Show'
				) {
					const { buy, rent, fast, free, flatrate, offerCount, maxOfferUpdatedAt } = data.node;
					const result: TitleOffers = {
						stream: [],
						rent,
						buy,
						free,
						fast,
						cinema: [],
						offerCount,
						maxOfferUpdatedAt,
					};
					flatrate.forEach(offer =>
						offer.monetizationType === MonetizationType.Cinema
							? result.cinema.push(offer)
							: result.stream.push(offer)
					);
					return result;
				}
			},
			skip() {
				return !['US', 'CA', 'AU', 'GB', 'UK'].includes(this.country);
			},
		},
	},
	setup({ titleNode }) {
		const title = titleNode;
		const { promotionOffer } = usePromotion({
			title,
			offers: () => {
				const { free = [], stream } = useApolloData<TitleOffers>('offers')();
				return [...free, ...stream];
			},
		});

		const { isArbitrating } = useArbitrage();

		return { promotionOffer, isArbitrating };

		// APPLE_QUALITY_TV_EXP
	},
})
export default class PromotedBuyBox extends Mixins(BackendTrackingMixin) {
	titleOffersPromotedData: TitleOffersPromotedData = {} as TitleOffersPromotedData;

	//APPLE_QUALITY_TV_EXP
	offers: TitleOffers = {
		stream: [],
		rent: [],
		buy: [],
		free: [],
		fast: [],
		cinema: [],
		offerCount: 0,
		maxOfferUpdatedAt: '',
	};
	@Prop({ required: false }) titleNode: TitleDetailsNode;

	declare promotionOffer: UsePromotion['promotionOffer'];
	declare isArbitrating: boolean;
	//APPLE_QUALITY_TV_EXP

	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleObjectType: NarrativeObjectType; // base title objectType
	@Prop({ required: true }) titleObjectId: number; // base title id
	@Prop({ default: null }) sponsoredAd: SponsoredAdFragment;

	// You need to pass this prop when title is Season/Episode, because BuyBox component has functionality to set
	// title into Watchlist, but this mutation available only for Movie/Show. So basically when you set Season/Episode
	// to Watchlist we set whole Show to watchlist.
	@Prop({ default: null }) showDetails: {
		id: string;
		objectId: number;
		objectType: ObjectType;
		title: string;
	};
	@Prop({ default: () => null }) seasonNumber: number | null;
	@Prop({ default: () => null }) episodeNumber: number | null;
	@Prop({ default: false }) onlyBundle: boolean;
	@Prop({ default: false }) isMiddleBuyBox: boolean;
	@Prop({ default: () => [] }) additionalContexts: (SnowplowModuleContext | SnowplowContext)[];

	// BUYBOX_EXP_4
	@Prop() buyboxExp4ValidationPassed: boolean;
	// BUYBOX_EXP_4

	// BUYBOX_EXP_5
	@Prop() buyboxExp5ValidationPassed: boolean;
	// BUYBOX_EXP_5

	// APPLE_BACKDROP_EXP
	@Prop({ required: false }) appleBackdropExpProps: AppleBackdropExpPropsType;
	// APPLE_BACKDROP_EXP

	@Language.Getter declare language: string;
	@Language.Getter declare country: string;

	// APPLE_QUALTIY_TV_EXP
	@Experiment.Getter activeVariantsWithControlGroup: Record<string, string>;
	@User.Getter buyboxPresentationType: OfferPresentationType;
	@User.Getter isLoggedIn: (providerName?: string) => boolean;

	isMounted = false;
	mounted() {
		this.isMounted = true;
	}
	// APPLE_QUALTIY_TV_EXP

	get title() {
		return {
			id: this.titleId,
			objectId: this.titleObjectId,
			objectType: this.titleObjectType,
		};
	}

	get queryVariables(): GetTitleOffersPromotedQueryVariables {
		return {
			nodeId: this.titleId,
			country: this.country,
			language: this.language,
		};
	}

	get promotedBundles() {
		return this.titleOffersPromotedData.promotedBundles || [];
	}

	get promotedOffers() {
		return (this.titleOffersPromotedData.promotedOffers as TitleOfferFragment[]) || [];
	}

	get hasPromotedOffers() {
		return this.promotedOffers?.length > 0;
	}

	get hasPromotedBundles() {
		return this.promotedBundles?.length > 0;
	}

	get categoryTranslatables() {
		return {
			[BuyboxOfferCategory.STREAM]: this.$t('WEBAPP_STREAM'),
			[BuyboxOfferCategory.RENT]: this.$t('WEBAPP_RENT'),
			[BuyboxOfferCategory.BUY]: this.$t('WEBAPP_BUY'),
			[BuyboxOfferCategory.BUNDLE]: this.$t('WEBAPP_BUNDLES'),
		};
	}

	get categories(): OfferCategories[] {
		if (this.onlyBundle) {
			return [BuyboxOfferCategory.BUNDLE];
		}

		const updatedOffers = (this.promotedOffers ?? [])
			.map(offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType))
			.filter(key => key !== undefined)
			.filter((item, index, offers) => offers.indexOf(item) === index) as OfferCategories[];

		if (this.hasPromotedBundles) {
			updatedOffers.push(BuyboxOfferCategory.BUNDLE);
		}

		updatedOffers.sort((a, b) => CategoryOrder.indexOf(a) - CategoryOrder.indexOf(b));

		return updatedOffers;
	}

	get sponsoredRecommendationCohort() {
		return this.sponsoredAd?.holdoutGroup ? '0' : '1';
	}

	// HARDCODED IT PROMOTED BUNDLE
	TimVisionBundle = {
		bundleId: 1048,
		iconUrl: `/${ASSETS_DIR}/img/timvision_white.webp`,
		icons: [
			'/icon/147638351/s100/disneyplus.{format}',
			'/icon/301462744/s100/netflixbasicwithads.{format}',
			`/${ASSETS_DIR}/img/primevideo_blue.webp`,
		],
	};
	// END HARDCODED BUNDLE

	showErrorToast() {
		toastError({ message: this.$t('WEBAPP_ALERT_ERROR_TITLE') as string, duration: 2000 });
	}

	// Temporarily filter out HBO offers in LATAM countries
	@Routing.State lastRoute: RoutingState['lastRoute'];

	getOffersByCategory(category: BuyboxOfferCategory): BuyboxRowOffer[] {
		if (category === BuyboxOfferCategory.BUNDLE) {
			return this.promotedBundles.map(promotedBundle => {
				const bundle: BuyboxRowOffer = {
					...promotedBundle.offer,
					standardWebURL: promotedBundle.promotionUrl,
					iconUrl: promotedBundle.node?.icon,
					bundleId: promotedBundle.node?.bundleId,
					bundleTechnicalName: promotedBundle.node?.technicalName,
				};

				// HARDCODED IT PROMOTED BUNDLE
				if (promotedBundle.node.bundleId === this.TimVisionBundle.bundleId) {
					bundle.iconUrl = this.TimVisionBundle.iconUrl;
					bundle.icons = this.TimVisionBundle.icons;
				}
				// END HARDCODED BUNDLE

				return bundle;
			});
		}

		// Temporarily filter out HBO offers in LATAM countries
		if (
			// prettier-ignore
			['BR', 'AR', 'MX', 'CL', 'CO', 'PE', 'EC', 'UY', 'CR', 'GT', 'DO', 'SV', 'BO', 'PA', 'HN', 'PY', 'NI'].includes(this.country) &&
			!this.isLoggedIn() &&
			!this.lastRoute?.name
		) {
			const filteredPromotedOffers = this.promotedOffers.filter(offer => {
				return ![1899, 384, 616].includes(offer.package.packageId);
			});

			return filteredPromotedOffers.filter(
				offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType) === category
			);
		} else {
			return this.promotedOffers.filter(
				offer => getBuyboxCategoryByMonetizationGraphql(offer.monetizationType) === category
			);
		}
	}

	clickOut(payload: { buyboxOffer: OfferGraphql; isBundle: boolean; evt: MouseEvent }) {
		const { buyboxOffer: offer, isBundle } = payload;
		const contexts = [...this.additionalContexts];

		if (isBundle) {
			const bundleWithOffer = this.promotedBundles.find(promotedBundle => promotedBundle.offer.id === offer.id);

			if (bundleWithOffer) {
				const packagesContext = TrackingHelper.getPackageContext(
					offer.package.packageId,
					offer.package.technicalName,
					'bundle',
					bundleWithOffer.node.bundleId,
					bundleWithOffer.node.technicalName
				);
				contexts.push(packagesContext);
			}
		}

		const sponsoredPackage = this.sponsoredAd?.campaign.watchNowOffer.package;
		if (this.sponsoredAd && payload.buyboxOffer.package.packageId === sponsoredPackage?.packageId) {
			const clickoutContext = SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer as Offer);
			const contexts = (this.additionalContexts || []).concat([
				new SnowplowTitleContextGraphql(this.titleObjectId, this.titleObjectType),
			]);
			contexts.push(clickoutContext);

			TrackingHelper.trackEvent(
				ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
				{
					action: 'buybox_clickout',
					label: `${this.titleId}_${sponsoredPackage.packageId}`,
					property: 'buybox',
					value: this.sponsoredRecommendationCohort,
				},
				contexts
			);

			// track influenced campaign metrics
			this.$backendTracking.trackSraEvent(SraEventAction.BuyboxClickout, this.sponsoredAd, this.titleId);
		}

		// BUYBOX_EXP_4
		if (this.buyboxExp4ValidationPassed) {
			const contexts = [SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer)];
			buyboxExp4TrackClickout(contexts);
		}
		// BUYBOX_EXP_4

		// BUYBOX_EXP_5
		if (this.buyboxExp5ValidationPassed) {
			const contexts = [SnowplowClickoutContextGraphql.fromProviderOffer(payload.buyboxOffer)];
			buyboxExp5TrackClickout(contexts);
		}
		// BUYBOX_EXP_5

		// APPLE_BACKDROP_EXP
		if (this.appleBackdropExpProps?.appleBackdropExpActive) {
			AppleBackdropExp.trackEvent({
				action: 'click',
				label: 'regular_offer',
				property: 'buybox',
			});
		}
		// APPLE_BACKDROP_EXP

		// APPLE_BACKDROP_EXP
		if (this.appleQualityTvExp.active) {
			AppleQualityTvExp.trackEvent({
				action: 'click',
				label: 'regular_offer',
				property: 'buybox',
			});
		}
		// APPLE_BACKDROP_EXP

		trackClickOutGraphql({
			offer,
			objectType: this.showDetails?.objectType ?? this.titleObjectType,
			objectId: this.showDetails?.objectId ?? this.titleObjectId,
			seasonNumber: this.seasonNumber,
			episodeNumber: this.episodeNumber,
			offerLocation: this.isMiddleBuyBox ? OfferLocation.MIDDLE_BUYBOX : OfferLocation.PROMOTED_BUYBOX,
			additionalContexts: contexts,
		});
	}
	// APPLE_QUALITY_TV_EXP

	get buyBoxRunning() {
		return this.country === 'US' && this.title.objectType === 'MOVIE';
	}
	get appleQualityTvExp() {
		const variant = this.activeVariantsWithControlGroup[AppleQualityTvExpName];
		const active =
			this.isMounted &&
			!!variant &&
			!this.isLoggedIn() &&
			this.promotionOffer?.id === 'PROMOTION_OFFER' &&
			this.promotionOffer.package.packageId == 350 &&
			!this.isArbitrating &&
			this.$route.name?.includes('app.detail') &&
			!this.buyBoxRunning;

		return {
			variant,
			active,
		};
	}
	get queryOfferVariables(): GetTitleOffersQueryVariables {
		return {
			nodeId: this.titleId,
			country: this.country,
			language: this.language,
			filterBuy: {
				monetizationTypes: [MonetizationType.Buy],
				bestOnly: true,
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
			},

			filterFlatrate: {
				monetizationTypes: [
					MonetizationType.Flatrate,
					MonetizationType.FlatrateAndBuy,
					MonetizationType.Ads,
					MonetizationType.Free,
					MonetizationType.Cinema,
				],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
			filterRent: {
				monetizationTypes: [MonetizationType.Rent],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
			filterFree: {
				monetizationTypes: [MonetizationType.Ads, MonetizationType.Free],
				presentationTypes: this.presentationTypeGraphql && [this.presentationTypeGraphql],
				bestOnly: true,
			},
		};
	}

	get presentationTypeGraphql() {
		// @ts-ignore
		const presentationTypeMap: Record<OfferPresentationType, PresentationType> = {
			[OfferPresentationType._4K]: PresentationType['4K'],
			[OfferPresentationType.HD]: PresentationType.Hd,
			[OfferPresentationType.SD]: PresentationType.Sd,
			[OfferPresentationType.DVD]: PresentationType.Dvd,
			[OfferPresentationType.BLURAY]: PresentationType.Bluray,
			[OfferPresentationType.CANVAS]: PresentationType.Canvas,
		};

		return presentationTypeMap[this.buyboxPresentationType];
	}
	// APPLE_QUALITY_TV_EXP
}
